import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom, map } from 'rxjs';
import { TYPESENSE_COLLECTIONS } from 'src/app/shared/constants/typesense-collections';
import { environment } from 'src/environments/environment';

// Define document interfaces
interface MusaffaIriDocument {
   id: string;
   last_update_time: string;
   status: string;
   iri_score: number;
   score_classification: string;
   community_impact_and_reputation: number;
   human_rights_and_labor_practices: number;
   product_and_sector_involvement: number;
   environmental_responsibility: number;
   ethical_governance: number;
}

interface CommunityIriDocument extends MusaffaIriDocument {
   musaffa_iri_id: string;
   user_count: number;
}

// Define collection types
type MusaffaIriCollection = {
   [key: string]: MusaffaIriDocument;
};

type CommunityIriCollection = {
   [key: string]: CommunityIriDocument;
};

@Injectable({
   providedIn: 'root',
})
export class IriDataService {
   private apiUrl = environment.apiUrl+'/api';
   private typeSenseUrl = environment.typeSense.url;

   private iriMusaffaData = `${this.typeSenseUrl}/collections/${TYPESENSE_COLLECTIONS.IRI_MUSAFFA_REPORT}/documents/`;
   private iriCommunityData = `${this.typeSenseUrl}/collections/${TYPESENSE_COLLECTIONS.IRI_COMMUNITY_REPORT}/documents/`;

   // Initialize empty collections
   private musaffaIriCollection: MusaffaIriCollection = {};
   private communityIriCollection: CommunityIriCollection = {};

   constructor(private http: HttpClient) {
      // if(environment.isUat) {
      //    this.typeSenseUrl = environment.uatTypeSense.url;
      // } else {
      //    this.typeSenseUrl = environment.typeSense.url;
      // }
      this.iriMusaffaData = `${this.typeSenseUrl}/collections/${TYPESENSE_COLLECTIONS.IRI_MUSAFFA_REPORT}/documents/`;
      this.iriCommunityData = `${this.typeSenseUrl}/collections/${TYPESENSE_COLLECTIONS.IRI_COMMUNITY_REPORT}/documents/`;
   }

   async fetchIriData(symbols: string[]): Promise<any> {
      const searches = [
         {
            collection: TYPESENSE_COLLECTIONS.IRI_MUSAFFA_REPORT,
            q: '*',
            filter_by: `id:=[${symbols.join(',')}]`,
         },
         // {
         //    collection: TYPESENSE_COLLECTIONS.IRI_COMMUNITY_REPORT,
         //    q: '*',
         //    filter_by: `id:=[${symbols.join(',')}]`,
         // }
      ]
      return await lastValueFrom(this.http.post<any>(environment.typeSense.url + '/multi_search', { searches }))
   }

   // Fetch Musaffa IRI data from TypeSense
   async fetchMusaffaIriData(
      symbol: string
   ): Promise<MusaffaIriDocument | null> {
      try {
         const response = await lastValueFrom(
            this.http.get<MusaffaIriDocument>(`${this.iriMusaffaData}${symbol}`)
         );

         if (response) {
            this.musaffaIriCollection[symbol] = response;
         }

         return response || null;
      } catch (error) {
         // console.error('Error fetching Musaffa IRI data:', error);
         return this.musaffaIriCollection[symbol] || null;
      }
   }

   // Fetch Musaffa IRI data from TypeSense
   async fetchMusaffaIriDataBySymbols(
      symbols: string[]
   ): Promise<any> {
      let filterStr = symbols.join(',');

         let queryParams = new HttpParams()
            .set('per_page', 250)
            .set('q', '*')
            .set('filter_by', `id:=[${filterStr}]`);

         const data = await lastValueFrom(
            this.http
               .get<any>(
                  `${this.iriMusaffaData}search`,
                  { params: queryParams }
               )
               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     return data;
                  })
               )
         );
      return data;
   }


   public async getCompanyProfileAndDataByCountryIsri(country: string = '', excludeSymbol: string = ''): Promise<any> {
      const items: any = {};
         const companyProfileFields = [
            'name',
            'logo',
            'cp_country',
            'city'
         ];

         let stocksDataFields: string[] = [
            'id',
            'sharia_compliance',
            'ranking',
            'ranking_v2',
            'musaffa_iri_score',
            'community_iri_score',
            'country',
            'price',
            'currentPrice'
         ];
         let queryParams = new HttpParams()
            .set('per_page', 15)
            .set('q', '*')
            .set('include_fields', `$${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}(${companyProfileFields.join(',')})${stocksDataFields ? ','+stocksDataFields.join(',') : ''}`)
            .set(
               'filter_by',
               `$${TYPESENSE_COLLECTIONS.COMPANY_PROFILE}(id:*)&&country:=[${country}]&&(musaffa_iri_score:>0||community_iri_score:>0)&&id:!=${excludeSymbol}`
             );

         await lastValueFrom(
            this.http
               .get<any>(
                  `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`,
                  { params: queryParams }
               )

               .pipe(
                  map((item) => {
                     const data: any = item.hits;
                     data.forEach((x: any) => {
                        // const doc = x.document;
                        const { [TYPESENSE_COLLECTIONS.COMPANY_PROFILE]: companyProfile, ...doc } = x.document;
                        items[doc.id] = {
                           ...companyProfile,
                           ...doc,
                           ticker: doc.id
                        };
                     });
                     return items;

                  })
               )
         );
      return items;
   }

   // Fetch Community IRI data from TypeSense
   async fetchCommunityIriData(
      symbol: string
   ): Promise<CommunityIriDocument | null> {
      try {
         const response = await lastValueFrom(
            this.http.get<CommunityIriDocument>(`${this.iriCommunityData}${symbol}`)
         );

         if (response) {
            this.communityIriCollection[symbol] = response;
         }

         // console.log('Community IRI data fetched:', response);

         return response || null;
      } catch (error) {
         // console.error('Error fetching Community IRI data:', error);
         return this.communityIriCollection[symbol] || null;
      }
   }

   // Data getters that ensure we have fresh data
   async getMusaffaIriData(
      symbol: string
   ): Promise<MusaffaIriDocument | null> {
      if (!this.musaffaIriCollection[symbol]) {
         await this.fetchMusaffaIriData(symbol);
      }
      const data = this.musaffaIriCollection[symbol];
      return data || null;
   }

   async getCommunityIriData(
      symbol: string
   ): Promise<CommunityIriDocument | null> {
      if (!this.communityIriCollection[symbol]) {
         await this.fetchCommunityIriData(symbol);
      }
      const data = this.communityIriCollection[symbol];
      // console.log("data:", data);
      return data || null;
   }

   async addRating(data: {
      user_id: string;
      email: string;
      company_symbol: string;
      main_ticker: string;
      is_deleted: string;
      community_impact_and_reputation: string;
      human_rights_and_labor_practices: string;
      product_and_sector_involvement: string;
      environmental_responsibility: string;
      ethical_governance: string;
   }): Promise<any> {
      try {
         const headers = new HttpHeaders().set('Content-Type', 'application/json');
         const response: any = await lastValueFrom(
            this.http.post(`${this.apiUrl}/iri/users/add-rating`, { ...data }, { headers })
         );

         if (response?.data?.status) {
            this.updateLocalCollections({
               company_symbol: data.company_symbol,
               community_impact: data.community_impact_and_reputation,
               human_rights: data.human_rights_and_labor_practices,
               product_sector: data.product_and_sector_involvement,
               environmental_resp: data.environmental_responsibility,
               ethical_governance: data.ethical_governance,
            });
         }

         return response;
      } catch (error) {
         console.error('Error adding IRI user rating:', error);
         return { status: 'success', message: 'Mock add rating success' };
      }
   }

   async removeRating(ticker: string): Promise<any> {
      try {
         const response: any = await lastValueFrom(
            this.http.get(`${this.apiUrl}/iri/users/remove-rating/${ticker}`)
         );
         return response;
      } catch (error) {
         console.error('Error removing IRI user rating:', error);
         return { status: 'success', message: `Mock remove rating for ${ticker}` };
      }
   }

   async getRating(ticker: string): Promise<any> {
      try {
         const response = await lastValueFrom(
            this.http.get(`${this.apiUrl}/iri/users/get-rating/${ticker}`)
         );
         console.log('getRating response:', response);
         return response;
      } catch (error) {
         console.error('Error fetching IRI rating:', error);
      }
   }

async updateUserRating(data: {
   user_id: string;
   email: string;
   company_symbol: string;
   main_ticker: string;
   is_deleted: string;
   community_impact_and_reputation: string;
   human_rights_and_labor_practices: string;
   product_and_sector_involvement: string;
   environmental_responsibility: string;
   ethical_governance: string;
}): Promise<any> {
   try {
      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const response: any = await lastValueFrom(
         this.http.post(`${this.apiUrl}/iri/users/add-rating`, { ...data }, { headers })
      );

      if (response?.data?.status) {
         this.updateLocalCollections({
            company_symbol: data.company_symbol,
            is_deleted: data.is_deleted, // Now always provided
            community_impact: data.community_impact_and_reputation,
            human_rights: data.human_rights_and_labor_practices,
            product_sector: data.product_and_sector_involvement,
            environmental_resp: data.environmental_responsibility,
            ethical_governance: data.ethical_governance,
         });

         // Clear cache for this symbol
         delete this.communityIriCollection[data.company_symbol];
      }

      return response;
   } catch (error) {
      console.error('Error updating IRI user rating:', error);
      throw error;
   }
}


private updateLocalCollections(data: {
   company_symbol: string;
   is_deleted?: string;  // Made optional with ?
   community_impact: string;
   human_rights: string;
   product_sector: string;
   environmental_resp: string;
   ethical_governance: string;
}): void {
   const symbol = data.company_symbol;
   const now = new Date().toISOString();
   const isRemoval = data.is_deleted === '1';

   if (this.communityIriCollection[symbol]) {
      if (isRemoval) {
         // Handle removal case
         this.communityIriCollection[symbol] = {
            ...this.communityIriCollection[symbol],
            last_update_time: now,
            user_count: Math.max(0, (this.communityIriCollection[symbol].user_count || 1) - 1)
         };
      } else {
         // Handle add/update case
         this.communityIriCollection[symbol] = {
            ...this.communityIriCollection[symbol],
            last_update_time: now,
            community_impact_and_reputation: parseFloat(data.community_impact),
            human_rights_and_labor_practices: parseFloat(data.human_rights),
            product_and_sector_involvement: parseFloat(data.product_sector),
            environmental_responsibility: parseFloat(data.environmental_resp),
            ethical_governance: parseFloat(data.ethical_governance),
            user_count: (this.communityIriCollection[symbol].user_count || 0) + 1
         };
      }
   }
}

   async refreshIriData(symbol: string): Promise<void> {
      try {
         await Promise.all([
            this.fetchMusaffaIriData(symbol),
            this.fetchCommunityIriData(symbol),
         ]);
         // console.log('All IRI data refreshed for symbol:', symbol);
      } catch (error) {
         console.error('Error refreshing IRI data:', error);
      }
   }

   async getUserRating(main_ticker: string): Promise<any> {
   // console.log('[DEBUG-API] getUserRating() - Calling API for:', main_ticker); // Debug log
   try {
      const response = await lastValueFrom(
         this.http.get(`${this.apiUrl}/iri/users/get-rating/${main_ticker}`)
      );
      // console.log('[DEBUG-API] getUserRating() - API response:', response); // Debug log
      return response;
   } catch (error) {
      // console.error('[DEBUG-API] Error in getUserRating:', error); // Debug log
      throw error;
   }
}
}
